/**
 * The detail behind a dashboard tile — /dev/browse/:kind
 *
 * The dashboard's tiles are counts. Clicking one lands here with the rows that
 * were counted, in the SAME columns the matching Excel download uses, because
 * the server builds both from one row builder (see mt5/devBrowse.js).
 *
 * EVERYTHING THAT NARROWS THE LIST LIVES IN THE URL — page, search, sort,
 * direction, rows per page. So a particular view can be linked, reloaded and
 * gone back to, and the browser's Back button steps through the list the way it
 * looks like it should. Keeping any of it in component state only would break
 * all three quietly.
 *
 * SEARCH AND SORT ARE SERVER-SIDE. Sorting the fifty rows already on screen and
 * calling it "sorted by net P&L" would be a lie about 721 deals, so the sort
 * goes to the database and the page comes back re-fetched.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowDownUp,
  ChevronLeft,
  ChevronRight,
  Download,
  LogOut,
  Moon,
  RefreshCw,
  Search,
  Sun,
  TriangleAlert,
  X,
} from "lucide-react";

import useDevTheme from "./useDevTheme";
import "./DeveloperDashboard.css";
import "./DeveloperBrowse.css";

const nf = (n) => Number(n || 0).toLocaleString();
const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The tiles, so this page can name itself before the first response arrives. */
const KIND_LABEL = {
  accounts: "Accounts",
  usages: "Account usages",
  pairs: "Pair dates",
  deals: "Deals, all kinds",
  trades: "Trades",
  cash: "Cash movements",
  settlements: "Settlements",
};

/**
 * Which download shape holds the same rows.
 *
 * Accounts and pair dates are the dashboard's own aggregates with no sheet of
 * their own, so they borrow the usages sheet — which is the closest thing that
 * actually contains them, rather than a download that would arrive empty.
 */
const DOWNLOAD_SHAPE = {
  accounts: "usages",
  usages: "usages",
  pairs: "usages",
  deals: "everything",
  trades: "trades",
  cash: "cash",
  settlements: "settlements",
};

const PAGE_SIZES = [25, 50, 100, 200];

export default function DeveloperBrowse() {
  const { kind } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  /* `theme` is not decoration here: every colour in DeveloperDashboard.css is
     namespaced under `.dev-root`, with the dark palette on
     `.dev-root.dev-theme-dark`. A wrapper carrying only `dev-root` therefore
     renders the LIGHT palette whatever the toggle says — which is exactly what
     this page did until the class was put back. */
  const { theme, toggle, dark } = useDevTheme();

  const token = localStorage.getItem("token");

  const page = Math.max(1, Number(params.get("page") || 1));
  const limit = Number(params.get("limit") || 50);
  const sort = params.get("sort") || "";
  const dir = params.get("dir") === "asc" ? "asc" : "desc";
  const q = params.get("q") || "";
  /* THE CONTAINER, arriving on the URL from the tile that linked here. This page
     is the detail behind one of the dashboard's cards, and that card was counted
     inside a container — so the listing has to be too, or a tile saying "804
     trades" would open a table of every container's trades. */
  const container = params.get("container") || "";

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  /* The box is typed into far faster than a query returns, so it holds its own
     value and pushes to the URL on a delay. Without that, every keystroke is a
     round trip and a history entry. */
  const [typed, setTyped] = useState(q);
  const firstRender = useRef(true);

  /** Change some of the URL state, resetting to page 1 unless the page IS the change. */
  const update = useCallback(
    (patch) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v === "" || v == null) next.delete(k);
        else next.set(k, String(v));
      }
      if (!("page" in patch)) next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  // Keep the box in step when the URL changes from anywhere else — Back, a link.
  useEffect(() => {
    setTyped(q);
  }, [q]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (typed === q) return;
    const t = setTimeout(() => update({ q: typed }), 300);
    return () => clearTimeout(t);
  }, [typed, q, update]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url =
        `/api/dev/export/browse/${encodeURIComponent(kind)}` +
        `?page=${page}&limit=${limit}` +
        (sort ? `&sort=${encodeURIComponent(sort)}` : "") +
        `&dir=${dir}` +
        (q ? `&q=${encodeURIComponent(q)}` : "") +
        (container ? `&container=${encodeURIComponent(container)}` : "");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401 || res.status === 403) {
        localStorage.clear();
        navigate("/login", { replace: true });
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || `failed (${res.status})`);
      setData(body);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [kind, page, limit, sort, dir, q, container, token, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  /** The export routes need a bearer token, which an <a href> cannot send. */
  const download = async () => {
    setDownloading(true);
    setError("");
    try {
      const shape = DOWNLOAD_SHAPE[kind] || "everything";
      /* THE CONTAINER GOES ON THE DOWNLOAD TOO. This is the one URL the UI
         assembles by hand rather than taking from the inventory, so it is the
         one that can fall out of step: without it, a listing scoped to one
         container offered a file covering every container, named as though it
         did not. `container=all` explicitly when nothing is selected, so the
         server is told rather than left to a fallback. */
      const res = await fetch(
        `/api/dev/export/all?only=${shape}&format=xlsx&container=${encodeURIComponent(container || "all")}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `download failed (${res.status})`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const named = /filename="?([^";]+)"?/.exec(cd);
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = named ? named[1] : `${kind}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const title = (data && data.title) || KIND_LABEL[kind] || kind;
  const columns = (data && data.columns) || [];
  const rows = (data && data.rows) || [];
  const pages = (data && data.pages) || 1;
  const total = (data && data.total) || 0;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);

  return (
    <div className={`dev-root dev-theme-${theme}`} data-dev-theme={theme}>
      <div className="dev-glow" aria-hidden="true" />

      <header className="dev-topbar">
        <div className="dev-brand">
          <Link
            /* Back to the dashboard IN THE SAME CONTAINER. Dropping it here
               would land the reader on the default after they had drilled into
               another one. */
            to={`/dev/dashboard${container ? `?container=${encodeURIComponent(container)}` : ""}`}
            className="dev-icon-btn dev-back"
            title="Back to the dashboard"
          >
            <ArrowLeft size={16} aria-hidden="true" />
          </Link>
          <div className="dev-titles">
            <h1 className="dev-title">{title}</h1>
            <p className="dev-sub">
              {loading && !data
                ? "loading…"
                : `${nf(total)} row${total === 1 ? "" : "s"}${q ? ` matching “${q}”` : ""}`}
            </p>
          </div>
        </div>

        <div className="dev-search">
          <Search size={15} aria-hidden="true" className="dev-search-icon" />
          <input
            type="search"
            className="dev-search-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setTyped("");
            }}
            placeholder="Search ticket, account, usage id, symbol, comment…"
            aria-label={`Search ${title}`}
          />
          {typed && (
            <button
              type="button"
              className="dev-search-clear"
              onClick={() => setTyped("")}
              aria-label="Clear the search"
              title="Clear"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="dev-topbar-actions">
          <button
            type="button"
            className="dev-ghost-btn"
            onClick={download}
            disabled={downloading}
            title="Download these rows as Excel"
          >
            <Download size={15} aria-hidden="true" />
            <span>{downloading ? "Preparing…" : "Excel"}</span>
          </button>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={load}
            aria-label="Reload"
            title="Reload"
          >
            <RefreshCw size={15} aria-hidden="true" className={loading ? "dev-spin" : undefined} />
          </button>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={toggle}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
            title={dark ? "Light theme" : "Dark theme"}
          >
            {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          </button>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={() => {
              localStorage.clear();
              navigate("/login", { replace: true });
            }}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main className="dev-main">
        {/* Named here as well as in the topbar: the topbar is the identity of the
            page, this is what the rows actually are. */}
        {data && data.subtitle && <p className="dev-browse-note">{data.subtitle}</p>}

        {error && (
          <p className="dev-warn">
            <TriangleAlert size={15} aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}

        <div className="dev-browse-bar">
          <div className="dev-browse-controls">
            {data && data.sorts && data.sorts.length > 0 && (
              <>
                <label className="dev-browse-lab" htmlFor="br-sort">
                  Sort
                </label>
                <select
                  id="br-sort"
                  className="dev-browse-select"
                  value={sort || (data.sorts[0] || "")}
                  onChange={(e) => update({ sort: e.target.value })}
                >
                  {data.sorts.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="dev-ghost-btn dev-browse-dir"
                  onClick={() => update({ dir: dir === "asc" ? "desc" : "asc" })}
                  title={dir === "asc" ? "Ascending — click for descending" : "Descending — click for ascending"}
                >
                  <ArrowDownUp size={14} aria-hidden="true" />
                  <span>{dir === "asc" ? "ascending" : "descending"}</span>
                </button>
              </>
            )}

            <label className="dev-browse-lab" htmlFor="br-size">
              Rows
            </label>
            <select
              id="br-size"
              className="dev-browse-select"
              value={limit}
              onChange={(e) => update({ limit: e.target.value })}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="dev-browse-page">
            <span className="dev-browse-range">
              {nf(from)}–{nf(to)} of {nf(total)}
            </span>
            <button
              type="button"
              className="dev-icon-btn"
              disabled={page <= 1}
              onClick={() => update({ page: page - 1 })}
              aria-label="Previous page"
              title="Previous"
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className="dev-browse-of">
              {nf(page)} / {nf(pages)}
            </span>
            <button
              type="button"
              className="dev-icon-btn"
              disabled={page >= pages}
              onClick={() => update({ page: page + 1 })}
              aria-label="Next page"
              title="Next"
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        {!loading && !error && rows.length === 0 && (
          <p className="dev-empty">
            {q ? `Nothing matches “${q}”.` : "There is nothing stored for this yet."}
          </p>
        )}

        {rows.length > 0 && (
          <div className="dev-browse-wrap">
            <table className="dev-browse-tbl">
              <thead>
                <tr>
                  {/* A row number, so a row can be referred to out loud. It counts
                      from the offset rather than from 1, or page 3 would start at
                      "1" again. */}
                  <th scope="col" className="dev-browse-idx">
                    #
                  </th>
                  {columns.map((c, i) => (
                    <th
                      scope="col"
                      key={`${c.label}-${i}`}
                      className={c.money ? "dev-browse-r" : undefined}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, ri) => (
                  <tr key={`${page}-${ri}`}>
                    <td className="dev-browse-idx">{nf(from + ri)}</td>
                    {r.map((cell, ci) => {
                      const col = columns[ci];
                      const isMoney = col && col.money && typeof cell === "number";
                      return (
                        <td
                          key={ci}
                          /* Only NEGATIVE money is tinted. Colouring gains too
                             turns a trade row into a wall of colour, because swap,
                             commission and fee are almost always negative and are
                             costs rather than losses — the eye needs the minus
                             signs to stand out, not every figure to shout. */
                          className={
                            (col && col.money ? "dev-browse-r " : "") +
                            (isMoney && cell < 0 ? "dev-neg" : "")
                          }
                          title={cell === "" || cell == null ? undefined : String(cell)}
                        >
                          {cell === "" || cell == null
                            ? ""
                            : isMoney
                            ? money(cell)
                            : String(cell)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
